// Duplicate delivery-note guard for the gate intake form.
//
// The KF49 census (2026-09-08) found four double captures and sixteen splits
// under a repeated note number — every one within the same day, so the old
// 24-hour in-memory warning had the rows in front of it and still lost them
// as a 10px line nobody read. This asks the SERVER whether the grower already
// used the note this season, hands the form a state it can show under the
// field and ask about on Save, and degrades honestly: a failed read is
// 'unknown' with the register's loaded window as the fallback, never a false
// "clear". Splits are legitimate, so this never refuses a save.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'

export interface DuplicateNoteRow {
  delivery_id:          string
  received_at:          string
  bins_received:        number
  status:               string
  grower:               string
  grower_id:            string | null
  delivery_note_number: string | null
}

interface FormLike {
  grower_id:            string
  grower:               string
  delivery_note_number: string
}

export type DuplicateNoteState =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'clear' }
  | { state: 'match'; match: DuplicateNoteRow; others: number }
  | { state: 'unknown'; fallback: DuplicateNoteRow | null }

/** Whitespace-collapsed, upper-cased note key — "r7 " and "R7" are one note. */
export function noteKey(s: string | null | undefined): string {
  return (s ?? '').trim().replace(/\s+/g, ' ').toUpperCase()
}

/** Same grower: by id when both sides have one, else by the typed name. */
export function sameGrower(
  a: { grower_id: string | null; grower: string },
  b: { grower_id: string | null; grower: string },
): boolean {
  if (a.grower_id && b.grower_id) return a.grower_id === b.grower_id
  return a.grower.trim().toLowerCase() === b.grower.trim().toLowerCase()
}

/** Newest earlier delivery of this grower on this note, plus how many more. */
export function findNoteMatch(
  rows: readonly DuplicateNoteRow[],
  form: FormLike,
): { match: DuplicateNoteRow; others: number } | null {
  const key = noteKey(form.delivery_note_number)
  if (!key) return null
  const me = { grower_id: form.grower_id || null, grower: form.grower }
  const hits = rows
    .filter(r => noteKey(r.delivery_note_number) === key && sameGrower(r, me))
    .sort((a, b) => b.received_at.localeCompare(a.received_at))
  return hits.length ? { match: hits[0], others: hits.length - 1 } : null
}

function escapeLike(s: string): string {
  return s.replace(/[\\%_]/g, m => '\\' + m)
}

/** 1 January of the current year, SAST — a note reused across seasons is not a double capture. */
function seasonStartIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-01-01T00:00:00+02:00`
}

const DEBOUNCE_MS = 400

export function useDuplicateNote(
  tenantId: string | null,
  form: FormLike,
  loadedRows: readonly DuplicateNoteRow[],
): DuplicateNoteState {
  const key = noteKey(form.delivery_note_number)
  // Debounced: one read per settled note, not one per keystroke.
  const [settled, setSettled] = useState(key)
  useEffect(() => {
    const t = setTimeout(() => setSettled(key), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [key])

  const enabled = !!tenantId && settled.length > 0
  const q = useQuery({
    queryKey: ['delivery-note-dup', tenantId, settled],
    enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(
        supabase
          .from('deliveries')
          .select('delivery_id, received_at, bins_received, status, grower, grower_id, delivery_note_number')
          .eq('tenant_id', tenantId as string)
          .is('deleted_at', null)
          .ilike('delivery_note_number', escapeLike(settled))
          .gte('received_at', seasonStartIso())
          .order('received_at', { ascending: false })
          .limit(50),
        'useDuplicateNote',
      )
      if (error) throw error
      return (data ?? []) as DuplicateNoteRow[]
    },
  })

  if (!key) return { state: 'idle' }
  if (key !== settled || (enabled && q.data === undefined && !q.isError)) return { state: 'checking' }
  // A failed REFETCH keeps the last data; the discriminator is data, not status.
  if (q.data !== undefined) {
    const hit = findNoteMatch(q.data, form)
    return hit ? { state: 'match', ...hit } : { state: 'clear' }
  }
  // Read failed or no tenant yet: say so, and show what the register already holds.
  return { state: 'unknown', fallback: findNoteMatch(loadedRows, form)?.match ?? null }
}
